<template>
  <div>
    <index-card fluid :loading="loading">
      <template slot="header">
        <v-row>
          <v-col class="white--text d-flex justify-space-between align-center headline">
            <div class="d-flex align-center">
              <v-btn icon color="blue-grey darken-1" :to="{ name: 'Home' }">
                <v-icon color="white">mdi-arrow-left</v-icon>
              </v-btn>
              <div>
                <div>{{ viewTitle }}</div>
                <div class="caption header-subtitle">{{ viewSubtitle }}</div>
              </div>
            </div>
            <div class="d-flex align-center">
              <BtnCreateDataExport
                class="mx-2"
                api_model="SellerComissionConfig"
                action="SellerComissionConfigs"
                :filters="{ ref_date: selected_date }"
                :file_name="$moment(selected_date).format('[Config_Comissoes_]MMMM[_]YYYY')"
              />
              <v-icon color="white" @click="getData()">mdi-refresh</v-icon>
            </div>
          </v-col>
        </v-row>
      </template>

      <template slot="subheader">
        <div class="view-switcher">
          <v-btn-toggle v-model="viewMode" mandatory dense class="view-toggle">
            <v-btn value="grid" small>
              <v-icon left small>mdi-table</v-icon>
              Grade
            </v-btn>
            <v-btn value="form" small>
              <v-icon left small>mdi-account-details</v-icon>
              Por Vendedor
            </v-btn>
          </v-btn-toggle>
          <span class="caption grey--text ml-3 d-none d-sm-inline">
            {{ viewHint }}
          </span>
          <v-spacer />
          <f-btn v-if="!isEditing && !isMobile" edit @click="startEdit" />
        </div>
      </template>

      <template slot="data_table">
        <v-card outlined>
          <SellerComissionGridView
            v-if="viewMode === 'grid'"
            :rows="rows"
            :search.sync="search"
            :month-label="monthLabel"
            :editing="isEditing"
            :copying="copying"
            :saving="saving"
            @prev-month="shiftMonth(-1)"
            @next-month="shiftMonth(1)"
            @copy-month="copyFromPreviousMonth"
            @add-seller="openAddDialog"
            @remove-seller="removeSeller"
            @cancel-edit="cancelEdit"
            @save-all="saveAll"
            @reload="getData"
            @dirty="onDirty"
          >
            <template #month>
              <MonthPicker
                :value="selected_date"
                :label="monthLabel"
                @input="onMonthInput"
              />
            </template>
          </SellerComissionGridView>

          <!-- Por Vendedor: formulário no desktop, cards no mobile -->
          <SellerComissionFormView
            v-else-if="!isMobile"
            :rows="rows"
            :selected="selected"
            :search.sync="search"
            :month-label="monthLabel"
            :editing="isEditing"
            :history="history"
            :history-loading="historyLoading"
            :copying="copying"
            :saving="saving"
            :resumo-fn="resumo"
            :initials-fn="initials"
            @prev-month="shiftMonth(-1)"
            @next-month="shiftMonth(1)"
            @select="selectSeller"
            @copy-seller="copyFromSeller"
            @copy-month="copyFromPreviousMonth"
            @add-seller="openAddDialog"
            @remove-seller="removeSeller"
            @cancel-edit="cancelEdit"
            @save-selected="saveSelected"
            @dirty="onDirty"
          >
            <template #month>
              <MonthPicker
                :value="selected_date"
                :label="monthShort"
                compact
                @input="onMonthInput"
              />
            </template>
          </SellerComissionFormView>

          <SellerComissionCardsView
            v-else
            :rows="rows"
            :search.sync="search"
            :editing="isEditing"
            :copying="copying"
            :saving="saving"
            :resumo-fn="resumo"
            :initials-fn="initials"
            @prev-month="shiftMonth(-1)"
            @next-month="shiftMonth(1)"
            @copy-month="copyFromPreviousMonth"
            @add-seller="openAddDialog"
            @remove-seller="removeSeller"
            @start-edit="startEdit"
            @cancel-edit="cancelEdit"
            @save-all="saveAll"
            @dirty="onDirty"
          >
            <template #month>
              <MonthPicker
                :value="selected_date"
                :label="monthShort"
                compact
                @input="onMonthInput"
              />
            </template>
          </SellerComissionCardsView>
        </v-card>
      </template>
    </index-card>

    <v-dialog v-model="addDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1">Adicionar vendedor</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="sellerToAdd"
            :items="availableSellers"
            :loading="addLoading"
            item-text="name"
            item-value="id"
            label="Vendedor ativo"
            dense
            hide-details
            no-data-text="Nenhum vendedor disponível"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="adding" @click="addDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :disabled="!sellerToAdd" :loading="adding" @click="confirmAdd">Adicionar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" timeout="3500" bottom>
      {{ snack.text }}
    </v-snackbar>
  </div>
</template>

<script>
import Api from '@/api/index'
import SellerComissionGridView from './SellerComissionGridView.vue'
import SellerComissionFormView from './SellerComissionFormView.vue'
import SellerComissionCardsView from './SellerComissionCardsView.vue'
import MonthPicker from './MonthPicker.vue'

const NUMERIC_FIELDS = [
  'cement_fixed_comission',
  'fassa_fixed_comission',
  'other_products_fixed_comission',
  'yard_cal_fixed_comission',
  'profitability_bonus',
  'profitability_margin_increase',
  'profitability_yard_margin',
  'profitability_gc_margin',
  'volume_goal',
  'volume_super_goal',
  'volume_hyper_goal',
  'client_goal',
  'client_super_goal',
  'client_hyper_goal',
  'profit_goal',
  'profit_super_goal',
  'profit_hyper_goal',
  'reactivated_clients_bonus',
  'new_clients_bonus',
]

const COPY_FIELDS = ['cement_comission_type', 'client_goal_type', ...NUMERIC_FIELDS]
const VIEW_STORAGE_KEY = 'seller_comission_configs_view'

export default {
  name: 'SellerComissionConfigsIndex',
  components: {
    BtnCreateDataExport: () => import('@/components/data_export/BtnCreateDataExport.vue'),
    IndexCard: () => import('@/_backoffice/_registrations/components/shared/index/IndexCard.vue'),
    SellerComissionGridView,
    SellerComissionFormView,
    SellerComissionCardsView,
    MonthPicker,
  },
  data() {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY)
    return {
      viewMode: ['grid', 'form'].includes(saved) ? saved : 'grid',
      selected_date: this.$moment().startOf('month').format('YYYY-MM-DD'),
      isEditing: false,
      loading: false,
      saving: false,
      copying: false,
      adding: false,
      addDialog: false,
      addLoading: false,
      availableSellers: [],
      sellerToAdd: null,
      historyLoading: false,
      search: '',
      rows: [],
      selectedId: null,
      history: [],
      snack: { show: false, text: '', color: 'success' },
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    monthLabel() {
      return this.$moment(this.selected_date).format('MMMM [de] YYYY')
    },
    monthShort() {
      return this.$moment(this.selected_date).format('MMMM YYYY')
    },
    previousMonthLabel() {
      return this.$moment(this.selected_date).subtract(1, 'month').format('MMM/YY')
    },
    previousMonthDate() {
      return this.$moment(this.selected_date).subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
    },
    selected() {
      return this.rows.find((r) => r.employee_id === this.selectedId) || null
    },
    dirtyCount() {
      return this.rows.filter((r) => r._dirty).length
    },
    viewTitle() {
      return this.viewMode === 'form' ? 'Comissões por Vendedor' : 'Configuração de Comissões'
    },
    viewSubtitle() {
      if (this.viewMode === 'form') {
        return this.isMobile
          ? `${this.rows.length} vendedores · ${this.monthLabel}`
          : `Bônus e comissão · ${this.monthLabel}`
      }
      return `Bônus e comissão por vendedor · ${this.rows.length} vendedores · ${this.monthLabel}`
    },
    viewHint() {
      if (this.viewMode === 'form') return 'Detalhe por vendedor com histórico'
      return 'Edição em planilha (todos os vendedores)'
    },
  },
  watch: {
    viewMode(val) {
      localStorage.setItem(VIEW_STORAGE_KEY, val)
      this.isEditing = false
      if (val === 'form' && !this.isMobile) this.loadHistory()
    },
    selected_date() {
      this.isEditing = false
      this.getData()
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    notify(text, color = 'success') {
      this.snack = { show: true, text, color }
    },
    onDirty() {},
    startEdit() {
      this.isEditing = true
    },
    async cancelEdit() {
      if (this.dirtyCount && !confirm('Há alterações não salvas. Descartar?')) return
      this.isEditing = false
      await this.getData()
    },
    onMonthInput(val) {
      if (val) this.selected_date = this.$moment(val).startOf('month').format('YYYY-MM-DD')
    },
    initials(name) {
      const parts = (name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      const a = parts[0][0] || ''
      const b = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (a + b).toUpperCase()
    },
    resumo(row) {
      if (!row) return ''
      const val = row.cement_fixed_comission != null ? Number(row.cement_fixed_comission) : 0
      if (row.cement_comission_type === 'per_revenue') return `Por faturamento · ${val}% fat.`
      return `Por saco · R$ ${val}/saco`
    },
    shiftMonth(delta) {
      this.selected_date = this.$moment(this.selected_date)
        .add(delta, 'month')
        .startOf('month')
        .format('YYYY-MM-DD')
    },
    flatten(payload) {
      const list = payload?.data || payload || []
      const seen = new Set()
      return (Array.isArray(list) ? list : [list]).reduce((rows, item) => {
        const attrs = item.attributes || item
        const employeeId = attrs.employee_id != null ? Number(attrs.employee_id) : null
        if (!employeeId || seen.has(employeeId)) return rows
        seen.add(employeeId)
        const row = {
          id: item.id || attrs.id || null,
          employee_id: employeeId,
          employee_name: attrs.employee_name,
          ref_date: attrs.ref_date,
          cement_comission_type: attrs.cement_comission_type || 'per_bag',
          client_goal_type: attrs.client_goal_type || 'fixed',
          _dirty: false,
        }
        NUMERIC_FIELDS.forEach((f) => {
          row[f] = attrs[f] != null ? Number(attrs[f]) : 0
        })
        rows.push(row)
        return rows
      }, [])
    },
    async getData() {
      this.loading = true
      try {
        const res = await Api.SellerComissionConfig.index({ ref_date: this.selected_date })
        this.rows = this.flatten(res.data)
        if (!this.rows.find((r) => r.employee_id === this.selectedId)) {
          this.selectedId = this.rows[0]?.employee_id || null
        }
        if (this.viewMode === 'form' && !this.isMobile) await this.loadHistory()
      } catch (e) {
        const err = e?.response?.data?.errors
        this.notify((Array.isArray(err) ? err[0] : err) || 'Erro ao carregar configurações', 'error')
      } finally {
        this.loading = false
      }
    },
    async selectSeller(row) {
      if (this.selected?._dirty && !confirm('Há alterações não salvas. Descartar e trocar de vendedor?')) return
      if (this.selected?._dirty) await this.getData()
      this.selectedId = row.employee_id
      if (!this.isMobile) await this.loadHistory()
    },
    async loadHistory() {
      this.history = []
      if (!this.selected?.id || this.isMobile) return
      this.historyLoading = true
      try {
        const res = await Api.SellerComissionConfig.history(this.selected.id)
        this.history = res.data?.history || []
      } catch (e) {
        this.history = []
      } finally {
        this.historyLoading = false
      }
    },
    payloadFrom(row) {
      const payload = {
        id: row.id,
        employee_id: row.employee_id,
        cement_comission_type: row.cement_comission_type,
        client_goal_type: row.client_goal_type,
      }
      NUMERIC_FIELDS.forEach((f) => {
        payload[f] = row[f] === '' || row[f] == null ? 0 : Number(row[f])
      })
      return payload
    },
    async saveAll() {
      const dirty = this.rows.filter((r) => r._dirty)
      if (!dirty.length) return
      this.saving = true
      try {
        await Api.SellerComissionConfig.bulkUpsert(
          this.selected_date,
          dirty.map((r) => this.payloadFrom(r))
        )
        this.notify('Configurações salvas')
        this.isEditing = false
        await this.getData()
      } catch (e) {
        const err = e?.response?.data?.errors
        this.notify((Array.isArray(err) ? err[0] : JSON.stringify(err)) || 'Erro ao salvar', 'error')
      } finally {
        this.saving = false
      }
    },
    async saveSelected() {
      if (!this.selected) return
      this.saving = true
      try {
        await Api.SellerComissionConfig.bulkUpsert(this.selected_date, [this.payloadFrom(this.selected)])
        this.notify('Configuração salva')
        this.isEditing = false
        const keepId = this.selected.employee_id
        await this.getData()
        this.selectedId = keepId
        if (!this.isMobile) await this.loadHistory()
      } catch (e) {
        const err = e?.response?.data?.errors
        this.notify((Array.isArray(err) ? err[0] : JSON.stringify(err)) || 'Erro ao salvar', 'error')
      } finally {
        this.saving = false
      }
    },
    copyFromSeller(source) {
      if (!this.isEditing || !this.selected || !source) return
      COPY_FIELDS.forEach((f) => {
        this.selected[f] = source[f]
      })
      this.selected._dirty = true
      this.notify(`Configuração de ${(source.employee_name || '').split(' ')[0]} aplicada`)
    },
    async copyFromPreviousMonth() {
      if (!this.isEditing) return
      if (!confirm(`Copiar configurações do mês anterior (${this.previousMonthLabel}) para ${this.monthLabel}?`)) return
      this.copying = true
      try {
        await Api.SellerComissionConfig.copyFromMonth(this.previousMonthDate, this.selected_date)
        this.notify('Configurações copiadas do mês anterior')
        const keepId = this.selectedId
        await this.getData()
        this.selectedId = keepId
        if (this.viewMode === 'form' && !this.isMobile) await this.loadHistory()
      } catch (e) {
        const err = e?.response?.data?.errors
        this.notify((Array.isArray(err) ? err[0] : err) || 'Erro ao copiar mês', 'error')
      } finally {
        this.copying = false
      }
    },
    async openAddDialog() {
      this.sellerToAdd = null
      this.addDialog = true
      this.addLoading = true
      try {
        const res = await Api.SellerComissionConfig.availableSellers(this.selected_date)
        this.availableSellers = (res.data?.data || res.data || []).reduce((list, s) => {
          const id = Number(s.id)
          if (!id || list.some((item) => item.id === id)) return list
          list.push({ id, name: s.name })
          return list
        }, [])
      } catch (e) {
        const err = e?.response?.data?.errors
        this.notify((Array.isArray(err) ? err[0] : err) || 'Erro ao listar vendedores', 'error')
        this.addDialog = false
      } finally {
        this.addLoading = false
      }
    },
    async confirmAdd() {
      if (!this.sellerToAdd) return
      this.adding = true
      try {
        await Api.SellerComissionConfig.addSeller(this.selected_date, this.sellerToAdd)
        this.notify('Vendedor adicionado')
        const keepId = Number(this.sellerToAdd)
        this.addDialog = false
        await this.getData()
        this.selectedId = keepId
        if (this.viewMode === 'form' && !this.isMobile) await this.loadHistory()
      } catch (e) {
        const err = e?.response?.data?.errors
        this.notify((Array.isArray(err) ? err[0] : err) || 'Erro ao adicionar vendedor', 'error')
      } finally {
        this.adding = false
      }
    },
    async removeSeller(row) {
      if (!row?.id) return
      if (!confirm(`Remover ${row.employee_name || 'este vendedor'} deste mês?`)) return
      try {
        await Api.SellerComissionConfig.destroy(row.id)
        await this.getData()
      } catch (e) {
        const err = e?.response?.data?.errors
        this.notify((Array.isArray(err) ? err[0] : err) || 'Erro ao remover vendedor', 'error')
      }
    },
  },
}
</script>

<style scoped>
.header-subtitle {
  opacity: 0.8;
  font-size: 12px !important;
  line-height: 1.2;
}
.view-switcher {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}
.view-toggle {
  background: #fff;
}
</style>
